import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Autocomplete,
  MenuItem,
  Chip,
  CircularProgress,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import EmplacementTree from "../../../Common/Components/EmplacementTree";

export default function AddUserModal({
  open,
  onClose,
  onAddUser,
  departments,
  userLang,
  userState,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
   const { t } = useTranslation(["Employee", "Common"]);
  const initialUserData = {
    isInactive: false,
    firstname: "",
    lastName: "",
    username: "",
    email: "",
    noTelephone: "",
    posteTelephonique: 0,
    nodooliz: "",
    lang: "FR",
    idDepartement: "",
    department: null,
    title: "",
    idEmplacements: [],
    posPwd: "",
  };

  const [userData, setUserData] = useState(initialUserData);
  const [treeData, setTreeData] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [saving, setSaving] = useState(false);

  // RESET FORM WHEN MODAL OPENS
  useEffect(() => {
    if (open) {
      setUserData(initialUserData);
    }
  }, [open]);

  // FETCH TREE
  useEffect(() => {
    const fetchTree = async () => {
      setLoadingTree(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Localization/GetProvinceTree`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          }
        );
        if (!response.ok) throw new Error("Erreur récupération tree");
        const data = await response.json();
        setTreeData(data || []);
      } catch (err) {
        console.error("Erreur TreeView:", err);
      } finally {
        setLoadingTree(false);
      }
    };

    if (open) fetchTree();
  }, [open, userState.token]);

  const handleChange = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      const body = {
        isInactive: userData.isInactive,
        firstname: userData.firstname,
        lastName: userData.lastName,
        username: userData.username,
        email: userData.email,
        noCellulaire: userData.noTelephone || null,
        posteTelephonique: parseInt(userData.posteTelephonique) || 0,
        nodooliz: userData.nodooliz,
        language: userData.lang || "FR",
        idDepartement: userData.idDepartement,
        title: userData.title,
        idEmplacements: userData.idEmplacements,
        department: userData.department,
        posPwd: userData.posPwd,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Employee/CreateInternalEmploye`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
            "Accept-Language": userData.lang || "FR",
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Erreur création utilisateur:", errorText);
        alert(errorText || "Impossible de créer l'utilisateur");
        return;
      }

      onAddUser(userData);
      onClose();
    } catch (err) {
      console.error("Erreur lors de l'appel API:", err);
      alert("Une erreur est survenue lors de la création de l'utilisateur.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          height: isMobile ? "100%" : "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            {t("AddUser")}
          </Typography>
          <Chip
            label={t("Insert")}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {t("InformationDepartment")}
        </Typography>
      </DialogTitle>

      <Divider />

      {/* CONTENT */}
      <DialogContent sx={{ flex: 1, overflow: "hidden", p: 3 }}>
        <Grid container spacing={3} sx={{ height: "100%" }}>
          {/* INFORMATIONS UTILISATEUR */}
          <Grid item xs={12} md={7} sx={{ height: "100%" }}>
            <Paper
              sx={{ height: "100%", display: "flex", flexDirection: "column", p: 3, borderRadius: 2, overflowY: "auto" }}
              variant="outlined"
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <InfoOutlinedIcon color="action" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Informations utilisateur
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nom d'utilisateur"
                    fullWidth
                    value={userData.username}
                    onChange={(e) => handleChange("username", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    fullWidth
                    type="email"
                    value={userData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prénom"
                    fullWidth
                    value={userData.firstname}
                    onChange={(e) => handleChange("firstname", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nom"
                    fullWidth
                    value={userData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Téléphone"
                    fullWidth
                    value={userData.noTelephone}
                    onChange={(e) => handleChange("noTelephone", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Poste téléphonique"
                    type="number"
                    fullWidth
                    value={userData.posteTelephonique}
                    onChange={(e) =>
                      handleChange("posteTelephonique", Number(e.target.value))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Langue"
                    fullWidth
                    value={userData.lang}
                    onChange={(e) => handleChange("lang", e.target.value)}
                  >
                    <MenuItem value="FR">Français</MenuItem>
                    <MenuItem value="EN">Anglais</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    fullWidth
                    options={departments || []}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option?.[userData.lang === "FR" ? "textFr" : "textEn"] || ""
                    }
                    value={userData.department || null}
                    onChange={(event, newValue) =>
                      setUserData((prev) => ({
                        ...prev,
                        department: newValue,
                        idDepartement: newValue?.id || "",
                      }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Département" fullWidth />
                    )}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                                    fullWidth
                                    label={t("POSpwd")}
                                    value={userData.posPwd || ""}
                                    onChange={(e) => handleChange("posPwd", e.target.value)}
                                    inputProps={{ maxLength: 5 }}
                                    error={userData.posPwd?.length > 5}
                                    helperText={
                                      userData.posPwd?.length > 5
                                        ? t("Maximum 5 digits allowed")
                                        : ""
                                    }
                                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Titre"
                    fullWidth
                    value={userData.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* EMPLACEMENTS */}
          <Grid item xs={12} md={5} sx={{ height: "100%" }}>
            <Paper
              sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2, borderRadius: 2 }}
              variant="outlined"
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <PlaceOutlinedIcon color="action" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Emplacements autorisés
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" mb={1}>
                Sélectionnez les emplacements accessibles
              </Typography>
              <Box sx={{ flex: 1, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                {loadingTree ? (
                  <Typography variant="body2">Chargement...</Typography>
                ) : (
                  <EmplacementTree
                    data={treeData || []}
                    language={userLang || "FR"}
                    selectedIds={userData.idEmplacements || []}
                    onChange={(ids) => handleChange("idEmplacements", ids)}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      {/* FOOTER */}
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Chip
          size="small"
          label={`${userData.idEmplacements.length} emplacement(s) sélectionné(s)`}
          variant="outlined"
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            {saving ? "Ajout..." : "Ajouter"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
